import OSS from 'ali-oss'

// 日志：开发环境打印，线上环境静默
const isDev = process.env.NODE_ENV === 'development'
const safeLog = (...args: any[]) => {
  if (isDev) console.log('[Uploader]', ...args)
}

// 自定义错误接口
interface CustomError extends Error {
  code?: string
  _reported?: boolean
}

// 格式化工具函数
const formatSpeed = (speed: number): string => {
  if (typeof speed !== 'number' || isNaN(speed)) return '0 B/s'
  if (speed < 1024) return `${speed.toFixed(1)} B/s`
  if (speed < 1024 * 1024) return `${(speed / 1024).toFixed(1)} KB/s`
  return `${(speed / 1024 / 1024).toFixed(1)} MB/s`
}

const formatTime = (seconds: number): string => {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '0s'
  if (seconds < 60) return `${Math.ceil(seconds)}s`
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = Math.ceil(seconds % 60)
  let result = ''
  if (hours > 0) result += `${hours}h`
  if (minutes > 0) result += `${minutes}m`
  result += `${remainingSeconds}s`
  return result
}

interface VideoMetadata {
  duration: number
  width: number
  height: number
}

const getVideoMetadata = (file: File | Blob | string): Promise<VideoMetadata> => {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('Invalid video file'))
    const video = document.createElement('video')
    let url = ''
    try {
      if (typeof file === 'string') url = file
      else if (file instanceof File || file instanceof Blob) url = URL.createObjectURL(file)
      else throw new Error('Invalid file type')

      video.onloadedmetadata = () => {
        resolve({
          duration: video.duration || 0,
          width: video.videoWidth || 0,
          height: video.videoHeight || 0,
        })
        if (typeof file !== 'string') URL.revokeObjectURL(url)
      }

      video.onerror = e => {
        if (typeof file !== 'string') URL.revokeObjectURL(url)
        reject(new Error(`Video metadata load error: ${e}`))
      }

      video.src = url
    } catch (error) {
      reject(new Error(`Failed to process video file: ${(error as Error).message}`))
    }
  })
}

const getImageSize = (
  file: File | Blob | string | any,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('Invalid image file'))
    const img = new Image()
    let url = ''
    try {
      if (typeof file === 'string') url = file
      else if (file instanceof File || file instanceof Blob) url = URL.createObjectURL(file)
      else if (file?.file) url = URL.createObjectURL(file.file)
      else throw new Error('Invalid file type')

      img.onload = () => {
        resolve({ width: img.width || 0, height: img.height || 0 })
        if (typeof file !== 'string') URL.revokeObjectURL(url)
      }

      img.onerror = () => {
        if (typeof file !== 'string') URL.revokeObjectURL(url)
        reject(new Error('Failed to load image'))
      }

      img.src = url
    } catch (error) {
      reject(new Error(`Failed to process image file: ${(error as Error).message}`))
    }
  })
}

export interface UploadStatus {
  id?: string
  fileName?: string
  status: 'PENDING' | 'UPLOADING' | 'SUCCESS' | 'FAIL'
  progress: number
  speed: string
  remainingTime: string
  url?: string
  cover?: string
  width?: number
  height?: number
  duration?: number
  error?: string
}

export const useOSSUploader = () => {
  // 取消分片上传
  const cancelUploadPart = async (uploadRequestId: string, client?: OSS, checkpoint?: any) => {
    try {
      if (client && checkpoint?.name && checkpoint?.uploadId) {
        safeLog('尝试中止OSS分片上传', checkpoint.name, checkpoint.uploadId)
        try {
          await client.abortMultipartUpload(checkpoint.name, checkpoint.uploadId)
          safeLog('分片上传中止成功')
        } catch (abortErr) {
          console.error('中止分片上传失败', abortErr)
        }
      }
      if (uploadRequestId) {
        await uploadService.cancelUploadBigFile({ uploadRequestId })
      }
    } catch (error) {
      console.error('取消上传失败', error)
    }
  }

  // 上报错误
  const reportUploadErrorMsg = async (
    file: File,
    err: CustomError,
    stsResponse: any,
    client?: OSS,
    checkpoint?: any,
  ) => {
    try {
      if (err._reported) {
        safeLog('错误已上报，跳过')
        return
      }
      const userStore = useUserStore()
      const { info } = storeToRefs(userStore)

      await uploadService.reportUploadErrorMsg({
        domain: info.value?.creatorDomain || '',
        errorCode: err.code || err.name || 'UnknownError',
        errorMsg: err.message || 'Unknown error',
        bucketName: stsResponse?.bucketName || '',
        objectKey: stsResponse?.objectKey || '',
        fileSize: file.size,
        uploadRequestId: stsResponse?.uploadRequestId || '',
      })

      err._reported = true
      if (stsResponse?.uploadRequestId) {
        await cancelUploadPart(stsResponse.uploadRequestId, client, checkpoint)
      }
    } catch (error) {
      console.error('上报错误信息失败', error)
    }
  }

  const upload = async (
    param: any,
    onSuccess?: (result: any) => void,
    onProgress?: (progress: number) => void,
    onError?: (error: Error) => void,
    onSpeedChange?: (speed: string) => void,
    onRemainingTimeChange?: (time: string) => void,
  ) => {
    const { file, uploadType } = param
    let stsResponse: any = null
    let objectKey = ''
    try {
      stsResponse = await uploadService.getSTSToken({
        fileName: file.file.name,
        totalSize: file.file.size,
        fileType: uploadType,
      })
      objectKey = stsResponse.objectKey
      let _duration = 0,
        _width = 0,
        _height = 0
      if (uploadType === 2) {
        const { duration, width, height } = await getVideoMetadata(file.file)
        _duration = duration
        _width = width
        _height = height
      } else if (uploadType === 1) {
        const { width, height } = await getImageSize(file.file)
        _width = width
        _height = height
      }

      if (stsResponse?.url) {
        onSuccess?.({
          url: stsResponse.url,
          videoUrl: stsResponse.url,
          coverBlurPic: stsResponse.coverBlurPic || '',
          coverPicUrl: stsResponse.cover || '',
          width: _width,
          height: _height,
          duration: _duration,
        })
        onProgress?.(100)
        return
      }

      const client = new OSS({
        region: stsResponse.region,
        accessKeyId: stsResponse.credentials.accessKeyId,
        accessKeySecret: stsResponse.credentials.accessKeySecret,
        bucket: stsResponse.bucketName,
        stsToken: stsResponse.credentials.securityToken,
        endpoint: stsResponse.endpoint,
        secure: true,
        timeout: 10800000, // 过期时间 3h
        refreshSTSToken: async () => {
          const newSTS = await uploadService.getSTSToken({
            fileName: file.file.name,
            totalSize: file.file.size,
            fileType: uploadType,
          })
          stsResponse.credentials = newSTS.credentials
          return {
            accessKeyId: stsResponse.credentials.accessKeyId,
            accessKeySecret: stsResponse.credentials.accessKeySecret,
            stsToken: stsResponse.credentials.securityToken,
          }
        },
        refreshSTSTokenInterval: 10 * 60 * 1000, // 10min
      })

      // 上传状态记录
      const partsStatus = {
        total: 0,
        completed: 0,
        failed: 0,
        retrying: false,
        checkpoint: null as any,
      }

      const startTime = Date.now()
      let lastLoaded = 0,
        lastTime = startTime
      const speedSamples: number[] = []
      const MAX_SPEED_SAMPLES = 5
      let prevAvgSpeed = 0

      const uploadOptions = {
        parallel: 2,
        partSize: stsResponse.fileUploadPartSize * 1024 * 1024,
        retryCount: 0,
        retryMax: 3,
        progress: (p: number, checkpoint: any) => {
          if (checkpoint?.doneParts) {
            partsStatus.total = checkpoint.fileSize / uploadOptions.partSize + 1
            partsStatus.completed = checkpoint.doneParts.length
            partsStatus.checkpoint = checkpoint
          }

          const currentTime = Date.now()
          const timeElapsed = (currentTime - lastTime) / 1000
          const loaded = p * file.file.size

          if (timeElapsed < 0.1) return

          const instantSpeed = (loaded - lastLoaded) / timeElapsed
          if (instantSpeed > 0) {
            speedSamples.push(instantSpeed)
            if (speedSamples.length > MAX_SPEED_SAMPLES) speedSamples.shift()
          }
          const avgSpeed =
            speedSamples.length > 0
              ? speedSamples.reduce((a, b) => a + b, 0) / speedSamples.length
              : instantSpeed
          const smoothedSpeed = prevAvgSpeed === 0 ? avgSpeed : avgSpeed * 0.5 + prevAvgSpeed * 0.5
          prevAvgSpeed = smoothedSpeed

          const remainingBytes = file.file.size - loaded
          const remainingTime = smoothedSpeed > 0 ? remainingBytes / smoothedSpeed : 0

          onProgress?.(Math.floor(p * 100))
          onSpeedChange?.(formatSpeed(smoothedSpeed))
          onRemainingTimeChange?.(formatTime(remainingTime))

          lastLoaded = loaded
          lastTime = currentTime
        },
        meta: {
          uid: Date.now(),
          pid: Date.now(),
        },
        mime: file.file.type,
        headers: {
          'Cache-Control': 'max-age=2592000', // 30 days
          'Content-Disposition': `attachment; filename=${encodeURIComponent(file.file.name)}`, // attachment 下载 // inline 预览
        },
      }

      const multipartUploadWithRetry = async (): Promise<any> => {
        safeLog('开始 multipartUploadWithRetry()')

        try {
          if (partsStatus.checkpoint?.uploadId) {
            let parts: any[] = []
            try {
              const res = await client.listParts(
                partsStatus.checkpoint.name,
                partsStatus.checkpoint.uploadId,
              )
              parts = Array.isArray(res.parts) ? res.parts : []
            } catch (e) {
              console.error('listParts 获取失败，继续重传', e)
            }

            const uploaded = new Set(parts.map(p => p.PartNumber))
            const totalParts = Math.ceil(file.file.size / uploadOptions.partSize)
            const remainingParts = Array.from({ length: totalParts }, (_, i) => i + 1).filter(
              p => !uploaded.has(p),
            )

            if (remainingParts.length > 0) {
              safeLog(`续传剩余 ${remainingParts.length} 个分片`)
              return await client.multipartUpload(partsStatus.checkpoint.name, file.file, {
                ...uploadOptions,
                checkpoint: {
                  ...partsStatus.checkpoint,
                  file: file.file,
                  partSize: uploadOptions.partSize,
                },
              })
            }
          }

          return await client.multipartUpload(stsResponse.objectKey, file.file, uploadOptions)
        } catch (error) {
          const err = error as CustomError
          console.error('multipartUploadWithRetry 出错', err)

          if (partsStatus.checkpoint) {
            try {
              await client.abortMultipartUpload(
                partsStatus.checkpoint.name,
                partsStatus.checkpoint.uploadId,
              )
            } catch (abortErr) {
              console.error('中止分片失败', abortErr)
            }
          }

          const isNetworkError =
            [
              'PartNotSequentialError',
              'ConnectionTimeoutError',
              'RequestError',
              'RequestTimeout',
              'NetworkError',
              'NoSuchUpload',
              'InvalidPart',
              'AccessDenied',
            ].includes(err.name) ||
            ['ERR_CONNECTION_RESET', 'ERR_NETWORK'].includes(err.code || '') ||
            err.message?.includes('upload part error') ||
            err.message?.includes('NetworkingError') ||
            err.message?.includes('net::ERR_CONNECTION_RESET') ||
            err.message?.includes('Failed to fetch')

          if (isNetworkError) {
            if (uploadOptions.retryCount < uploadOptions.retryMax) {
              uploadOptions.retryCount++
              partsStatus.retrying = true
              const retryDelay = Math.min(1000 * 2 ** (uploadOptions.retryCount - 1), 10000)
              safeLog(
                `网络异常重试中，第 ${uploadOptions.retryCount} 次，等待 ${retryDelay / 1000}s`,
              )
              onSpeedChange?.('Retrying...')
              await new Promise(resolve => setTimeout(resolve, retryDelay))
              partsStatus.retrying = false
              return await multipartUploadWithRetry()
            } else {
              console.error('已达最大重试次数')
              await reportUploadErrorMsg(
                file.file,
                err,
                stsResponse,
                client,
                partsStatus.checkpoint,
              )
              throw err
            }
          } else {
            await reportUploadErrorMsg(file.file, err, stsResponse, client, partsStatus.checkpoint)
            throw err
          }
        }
      }

      const result = await multipartUploadWithRetry()

      const url = (result.res as any)?.requestUrls?.[0]?.split('?')[0] || ''
      const reportRes = await uploadService.reportBigFileUrl({
        fileUrl: url,
        fileType: uploadType,
        uploadRequestId: stsResponse.uploadRequestId,
      })

      if (reportRes.fileUrl) {
        onSuccess?.({
          url: reportRes.fileUrl,
          videoUrl: reportRes.fileUrl,
          coverBlurPic: reportRes.coverBlurPic || '',
          coverPicUrl: reportRes.cover || '',
          width: _width,
          height: _height,
          duration: _duration,
        })
      } else {
        const uploadFailedErr = new Error('upload failed') as CustomError
        await reportUploadErrorMsg(
          file.file,
          uploadFailedErr,
          stsResponse,
          client,
          partsStatus.checkpoint,
        )
        onError?.(uploadFailedErr)
      }
    } catch (error) {
      const err = error as CustomError
      await reportUploadErrorMsg(file.file, err, stsResponse)
      onError?.(err)
      throw err
    }
  }

  return { upload, cancelUploadPart, formatTime, getVideoMetadata, getImageSize }
}
